/**
 * Client ของการนำเข้า Forecast ลูกค้า (`/v1/forecast-imports`, D-093) ตาม payload
 * contract ที่ freeze ไว้ใน `docs/API_CONTRACT.md` หัวข้อ "Forecast Import
 * (`PLANNING`) — Phase 2"
 *
 * **client ใหม่ทั้งตัว ไม่ใช่ผู้ใช้ของ `lib/sales` หรือ `lib/procurement`** — โครง
 * ชื่อฟังก์ชัน และชุดความล้มเหลวถูกเขียนใหม่ที่นี่โดยตั้งใจ ไม่ได้ import ข้ามกลุ่ม:
 * `PLANNING` เป็นคนละโมดูลสิทธิ์ คนละชุดกฎธุรกิจ และเป็นงานของคนละแผนก การผูกให้ใช้
 * abstraction ร่วมกันแปลว่าวันที่ contract ฝั่งหนึ่งเปลี่ยน อีกฝั่งจะเปลี่ยนตามโดย
 * ไม่มีใครตั้งใจ
 *
 * หลักการเดียวกับ client อื่นทุกตัวในโปรเจกต์นี้:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` **ไม่ใช่ "ยังไม่มี forecast"** — รายการ
 *   ว่างกับโหลดไม่สำเร็จต้องแยกกันให้ขาด
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * contract ของกลุ่มนี้ freeze ไว้ **สี่** operation และไฟล์นี้ implement ครบทั้งสี่
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateForecastImportBody,
  ForecastImportBatch,
  ForecastImportBatchSummary,
  ForecastImportListPage,
  ForecastLine,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ
 */
export const FORECAST_IMPORTS_PATH = '/v1/forecast-imports';

/**
 * เส้นทางของ batch เดียว — `batch_id` เป็น **UUID surrogate** (D-039) ไม่ใช่
 * `version_label` ซึ่งเป็นชื่อที่ลูกค้าตั้งและซ้ำกันได้
 *
 * `encodeURIComponent` ไว้ด้วยแม้ UUID จะไม่มีอักขระที่ต้อง escape: ค่าที่เดินทางมา
 * ถึงที่นี่มาจาก payload ของเซิร์ฟเวอร์ และค่าที่อ่านมาแล้วแปะลง URL ดิบ ๆ คือวิธีที่
 * คำขอจะเปลี่ยน**เส้นทาง**ทั้งเส้นในวันที่ payload ไม่ใช่สิ่งที่คิดไว้ — ซึ่งในกลุ่มนี้
 * แปลว่า `POST` ที่ตั้งใจจะอ่านใบหนึ่ง ไปโผล่ที่เส้น publish ของอีกใบ
 */
export function forecastImportPath(batchId: string): string {
  return `${FORECAST_IMPORTS_PATH}/${encodeURIComponent(batchId)}`;
}

/** `POST /{batch_id}/publish` — เส้นเดียวของ lifecycle ในกลุ่มนี้ */
export function forecastImportPublishPath(batchId: string): string {
  return `${forecastImportPath(batchId)}/publish`;
}

/**
 * พารามิเตอร์ของ `GET /v1/forecast-imports` ที่ contract รับ — และไม่มีตัวที่หก
 *
 * **ไม่มีพารามิเตอร์ค้นหาอิสระเลย** ช่องค้นหาบนหน้าจอจึงกรอง **ฝั่งเบราว์เซอร์จาก
 * หน้าที่โหลดมาแล้ว** ไม่ใช่ยิงคำขอใหม่ (ดู `forecastsView.ts`) ส่วน `status`,
 * `plant_code` และ `customer_code` เป็นตัวกรองแบบตรงตัวที่เซิร์ฟเวอร์รับจริง
 *
 * `plant_code` **แคบลงได้เท่านั้น ไม่เคยกว้างขึ้น** — เซิร์ฟเวอร์กรองตาม
 * `plant_scope` ของผู้เรียกก่อนเสมอ และโรงงานนอกขอบเขตตอบ `403` ไม่ใช่หน้าว่าง
 */
export interface ForecastImportListQuery {
  readonly plant_code?: string;
  readonly customer_code?: string;
  readonly status?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * ประกอบ query string — คีย์ที่ไม่ได้ระบุ (หรือว่างเปล่า) ไม่เดินทางไปเลย
 *
 * `cursor` ที่เป็นสตริงว่างถูกทิ้ง ไม่ใช่ส่งไปเป็นค่าว่าง: cursor ว่างไม่ใช่ "หน้าแรก"
 * สำหรับเซิร์ฟเวอร์ แต่เป็นค่าที่มันจะปฏิเสธว่าไม่ใช่ cursor ที่ตนออกให้
 */
export function buildForecastImportListQuery(
  query: ForecastImportListQuery = {},
): string {
  const params = new URLSearchParams();
  const plantCode = query.plant_code?.trim() ?? '';
  if (plantCode !== '') {
    params.append('plant_code', plantCode);
  }
  const customerCode = query.customer_code?.trim() ?? '';
  if (customerCode !== '') {
    params.append('customer_code', customerCode);
  }
  const status = query.status?.trim() ?? '';
  if (status !== '') {
    params.append('status', status);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ ในกลุ่มนี้มันเป็นตัวเดียวที่แยก
   * `FORECAST_BATCH_IS_INVALID` (ใบมีแถวผิด แก้ไฟล์แล้วนำเข้าใหม่) ออกจาก
   * `FORECAST_BATCH_NOT_VALIDATED` (ใบผ่านจุดนั้นไปแล้ว ไม่มีอะไรต้องแก้)
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * `403` ของกลุ่มนี้มีสองความหมายที่ `details.rule` เป็นตัวแยก: `PLANT_OUTSIDE_SCOPE`
 * คือ **โรงงานที่ขอไม่อยู่ในขอบเขตของคุณ** (D-004) ส่วนไม่มี rule คือ **แผนกของคุณ
 * ไม่ได้ถือโมดูล `PLANNING` ในระดับนั้น** — คนละเรื่องกันคนละทางแก้ และเพราะ
 * `PLANNING` เป็นโมดูลธุรกิจปกติ `ADMIN` ก็ถูกปฏิเสธด้วย `403` เช่นกัน (D-060)
 *
 * `conflict` (`409`) ต้องเป็นสถานะของตัวเอง **ไม่ใช่ `server-error`**: มันคือ "ใบนี้
 * ไม่ได้อยู่ที่ VALIDATED แล้ว" ซึ่งมักแปลว่ามีคนกด publish ไปก่อน — ผู้ใช้จัดการต่อ
 * เองได้ด้วยการโหลดรายการใหม่ ส่วน "เซิร์ฟเวอร์ทำงานไม่สำเร็จ" คือสิ่งที่เขาทำอะไร
 * ไม่ได้เลย
 *
 * `rule-violation` (`422`) เป็นที่อยู่ของกฎที่แพงที่สุดในกลุ่มนี้ —
 * `FORECAST_BATCH_IS_INVALID` การกลืนมันเป็น `forbidden` จะส่งผู้กดไปขอสิทธิ์เพิ่ม
 * เพื่อแก้สิ่งที่ไม่ใช่เรื่องสิทธิ์เลยแม้แต่น้อย
 */
export type ForecastImportFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type ForecastImportListResult =
  | { readonly status: 'ready'; readonly page: ForecastImportListPage }
  | ForecastImportFailure;

/**
 * คำตอบของทั้งสามเส้นที่คืน batch **พร้อมบรรทัด** — `GET /{batch_id}`, `POST` และ
 * `POST /{batch_id}/publish`
 *
 * **รูปเดียวกันทั้งสามเส้น** ตามที่ contract ประกาศ (`201` จากเส้นแรก และ `200` จาก
 * อีกสองเส้น) — และการบังคับให้ publish คืนบรรทัดมาด้วยเป็นการตัดสินใจ ไม่ใช่ความ
 * บังเอิญ: หน้าจอที่เพิ่ง publish สำเร็จต้องแสดง batch ที่มีสถานะใหม่ได้ทันทีโดย
 * ไม่ต้องยิงคำขอที่สอง
 */
export type ForecastImportDetailResult =
  | { readonly status: 'ready'; readonly batch: ForecastImportBatch }
  | ForecastImportFailure;

export interface ForecastImportRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ: คีย์ที่หายไปทั้งคีย์
 * ถือเป็น `null` ส่วนคีย์ที่มาเป็นตัวเลขหรือ object คือ payload ที่หน้าจอไม่ควรแสดง
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/** จำนวนเต็มที่ไม่ติดลบ หรือ `null` เมื่อคีย์หายไป — `undefined` = ผิดรูป */
function nullableCount(value: unknown): number | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    return undefined;
  }
  return value;
}

/**
 * `next_cursor` ของ envelope — `undefined` แปลว่า "ผิดรูป" ไม่ใช่ "ไม่มีหน้าถัดไป"
 *
 * cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่างจะวน
 * ขอหน้าเดิมซ้ำไปเรื่อย ๆ
 */
function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/** `YYYY-MM-DD` ที่เป็นวันที่มีอยู่จริง — ไม่ผ่าน `Date` แม้แต่ครั้งเดียว */
const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `forecast_date` ที่อ่านได้ — **บังคับรูป `YYYY-MM-DD` ล้วน**
 *
 * ค่าที่มี `T`, มี offset หรือมีเวลาติดมา ถูกปฏิเสธเป็น `null` (= ทั้ง batch ผิดรูป)
 * แทนที่จะถูกตัดให้เหลือแต่วัน: คอลัมน์ฝั่งเซิร์ฟเวอร์เป็น `DATE` เพราะ forecast ของ
 * วันเดียวกันต้องไม่ตกคนละ bucket (D-093) หน้าจอที่ยอมรับ instant แล้วเลือกวันออกมา
 * เอง คือหน้าจอที่พาบั๊กนั้นกลับมาจากทางฝั่ง client
 */
function parseForecastDate(value: unknown): string | null {
  const text = requiredString(value);
  if (text === null || !CALENDAR_DATE_PATTERN.test(text)) {
    return null;
  }
  return text;
}

/**
 * หนึ่งบรรทัด — **บังคับเฉพาะฟิลด์ที่บรรทัดใดก็ตามต้องมี**
 *
 * `line_no`, `forecast_date`, `quantity` และ `uom_code` บังคับ เพราะทั้งสี่ตัวถูก
 * เก็บเป็น `NOT NULL` และเป็นสิ่งที่ผู้ใช้ส่งมาเอง — บรรทัดที่ขาดตัวใดตัวหนึ่งจึงเป็น
 * บรรทัดที่เขียนลงฐานข้อมูลไม่ได้ตั้งแต่แรก
 *
 * `product_code` **ไม่บังคับ** และนั่นคือหัวใจของกลุ่มนี้: บรรทัดที่ได้
 * `PRODUCT_NOT_FOUND` มี `product_code` เป็น `null` เพราะไม่มีอะไรให้ resolve การ
 * บังคับมันจะทำให้ **บรรทัดที่ผิดที่สุดในไฟล์ กลายเป็นบรรทัดที่แสดงไม่ได้** ซึ่ง
 * ตรงข้ามกับเหตุผลที่หน้าจอนี้มีอยู่
 */
export function parseForecastLine(raw: unknown): ForecastLine | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const lineNo = record.line_no;
  if (typeof lineNo !== 'number' || !Number.isInteger(lineNo)) {
    return null;
  }

  const forecastDate = parseForecastDate(record.forecast_date);
  const quantity = requiredString(record.quantity);
  const uomCode = requiredString(record.uom_code);
  if (forecastDate === null || quantity === null || uomCode === null) {
    return null;
  }

  const productId = nullableString(record.product_id);
  const productCode = nullableString(record.product_code);
  const validationError = nullableString(record.validation_error);
  if (
    productId === undefined ||
    productCode === undefined ||
    validationError === undefined
  ) {
    return null;
  }

  return {
    line_no: lineNo,
    product_id: productId,
    product_code: productCode,
    forecast_date: forecastDate,
    quantity,
    uom_code: uomCode,
    // สตริงว่างไม่ใช่ token — ถือเป็น "บรรทัดนี้ผ่าน" เท่ากับ `null`
    validation_error:
      validationError === null || validationError.trim() === '' ? null : validationError,
  };
}

/**
 * หัว batch หนึ่งใบ **โดยไม่แตะคีย์ `lines` เลย** — ใช้กับแถวในรายการ
 *
 * **ห้าฟิลด์ที่บังคับคือห้าฟิลด์ที่ contract เอ่ยชื่อไว้ตรง ๆ** (`batch_id` จาก
 * เส้นทาง `/{batch_id}`, `plant_code`, `customer_code`, `version_label` จาก `POST`
 * body และ `status` จาก lifecycle) ส่วนที่เหลือเป็น nullable ที่หายไปทั้งคีย์ได้ —
 * และนั่นเป็นการตัดสินใจ ไม่ใช่ความหละหลวม:
 *
 * contract ของกลุ่มนี้ **ไม่มีตัวอย่าง response ทีละฟิลด์** (มีแต่ตัวอย่างของ `POST`
 * body) การบังคับชื่อฟิลด์ประกอบที่เดาเอาเอง เช่น `published_by_employee_id` หรือ
 * `plant_id` แล้วเซิร์ฟเวอร์ตั้งชื่อไว้ต่างออกไป จะทำให้ **ทั้งรายการ** กลายเป็น
 * `invalid-response` ทั้งที่ทุกอย่างทำงานถูกต้อง ผู้ใช้จะเห็นหน้าจอที่บอกว่าข้อมูล
 * ผิดรูปแทนที่จะเห็น forecast ของตัวเอง ส่วนความเสี่ยงฝั่งตรงข้าม (ฟิลด์ประกอบหาย
 * ไปจริง) มีผลแค่ค่าที่หน้าจอแสดงเป็น "—"
 *
 * `status` ถูกอ่านเป็นสตริงอะไรก็ได้ที่ไม่ว่าง ไม่ใช่ union ของห้าค่า: CHECK
 * constraint ฝั่งเซิร์ฟเวอร์เป็นผู้ถือชุดที่รับได้ และการกรองค่าที่หกทิ้งตรงนี้จะทำ
 * ให้ **ใบที่มีอยู่จริงหายไปจากรายการ** ในวันที่ชุดนั้นถูกขยาย ซึ่งแย่กว่าการแสดง
 * รหัสที่หน้าจอยังไม่มีคำอธิบายให้ (ดู `forecastBatchStatusLabel`) — และในกลุ่มนี้
 * แถวที่หายไปหนึ่งแถวคือ forecast ของลูกค้าที่ไม่มีใครรู้ว่ามีอยู่
 */
export function parseForecastImportSummary(
  raw: unknown,
): ForecastImportBatchSummary | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const batchId = requiredString(record.batch_id);
  const plantCode = requiredString(record.plant_code);
  const customerCode = requiredString(record.customer_code);
  const versionLabel = requiredString(record.version_label);
  const status = requiredString(record.status);

  if (
    batchId === null ||
    plantCode === null ||
    customerCode === null ||
    versionLabel === null ||
    status === null
  ) {
    return null;
  }

  const plantId = nullableString(record.plant_id);
  const customerId = nullableString(record.customer_id);
  const sourceFileHash = nullableString(record.source_file_hash);
  const createdBy = nullableString(record.created_by_employee_id);
  const publishedBy = nullableString(record.published_by_employee_id);
  const publishedAt = nullableString(record.published_at);
  const supersededBy = nullableString(record.superseded_by_batch_id);
  const createdAt = nullableString(record.created_at);
  const updatedAt = nullableString(record.updated_at);
  const lineCount = nullableCount(record.line_count);

  if (
    plantId === undefined ||
    customerId === undefined ||
    sourceFileHash === undefined ||
    createdBy === undefined ||
    publishedBy === undefined ||
    publishedAt === undefined ||
    supersededBy === undefined ||
    createdAt === undefined ||
    updatedAt === undefined ||
    lineCount === undefined
  ) {
    return null;
  }

  return {
    batch_id: batchId,
    plant_id: plantId,
    plant_code: plantCode,
    customer_id: customerId,
    customer_code: customerCode,
    version_label: versionLabel,
    status,
    source_file_hash: sourceFileHash,
    line_count: lineCount,
    created_by_employee_id: createdBy,
    published_by_employee_id: publishedBy,
    published_at: publishedAt,
    superseded_by_batch_id: supersededBy,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * batch เต็มใบ **พร้อมบรรทัด** — `GET /{batch_id}`, `201` ของ `POST` และคำตอบของ
 * publish
 *
 * **บรรทัดต้องอ่านออกทุกบรรทัด ไม่งั้นทั้งใบผิดรูป** บรรทัดที่หายไปเงียบ ๆ หนึ่ง
 * บรรทัดคือแถวที่ผู้ใช้จะไม่เห็นว่าผิด แล้วเขาจะแก้ไฟล์ไม่ครบ นำเข้าใหม่ แล้วได้
 * `INVALID` อีกครั้งโดยไม่เข้าใจว่าทำไม — ซึ่งเป็นความเสียหายชนิดเดียวกับที่ flow
 * ข้อ 1.3 เขียนกฎ "ห้ามทิ้งแถวที่ผิดเงียบ ๆ" ขึ้นมาป้องกัน
 */
export function parseForecastImportBatch(raw: unknown): ForecastImportBatch | null {
  const summary = parseForecastImportSummary(raw);
  if (summary === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.lines)) {
    return null;
  }

  const lines: ForecastLine[] = [];
  for (const entry of record.lines) {
    const line = parseForecastLine(entry);
    if (line === null) {
      return null;
    }
    lines.push(line);
  }

  return { ...summary, lines };
}

/**
 * อ่านรายการจาก envelope `{ items, next_cursor }` — **และจากที่นั่นเท่านั้น**
 *
 * contract freeze รูปนี้ไว้ตรง ๆ จึงไม่รับ array เปล่าเป็นทางถอย
 *
 * แต่ละ item ถูกอ่านด้วย `parseForecastImportSummary()` ซึ่ง **ไม่แตะคีย์ `lines`
 * เลย** ต่อให้เซิร์ฟเวอร์ส่งมาด้วยก็ตาม — contract ตัดบรรทัดออกจากรายการ และหน้าจอ
 * ที่แอบใช้บรรทัดจากรายการได้ในวันหนึ่ง จะพังเงียบ ๆ ในวันที่เซิร์ฟเวอร์เลิกส่งมา
 */
export function parseForecastImportListPage(
  raw: unknown,
): ForecastImportListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: ForecastImportBatchSummary[] = [];
  for (const entry of record.items) {
    const parsed = parseForecastImportSummary(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ---------------------------------------------------------- serializing -- */

/**
 * แปลง body ของ `POST /v1/forecast-imports` ให้เหลือเฉพาะคีย์ที่ contract รับ
 *
 * เขียนคีย์ที่ออกไปทีละตัวแทนการ spread state ของฟอร์ม: `extra = "forbid"` ฝั่ง
 * เซิร์ฟเวอร์ทำให้คีย์แปลกปลอมที่ติดมากลายเป็น `400
 * FORECAST_IMPORT_FIELD_IS_SERVER_OWNED` ทั้งที่ผู้ใช้กรอกถูกทุกช่อง — และเป็นด่านที่
 * ทำให้ฟิลด์ที่เซิร์ฟเวอร์เป็นเจ้าของไปถึงเซิร์ฟเวอร์ **ไม่ได้เลย** ต่อให้ผู้เรียก
 * ถือ object ที่มีคีย์นั้นอยู่ในมือ
 *
 * ที่แพงที่สุดคือสองคีย์นี้: `status` ที่หลุดไปแปลว่ามีคนพยายามข้ามการตรวจ (batch ที่
 * เกิดมาเป็น `VALIDATED` เองคือ batch ที่ไม่มีใครตรวจ) และ `validation_error` ราย
 * บรรทัดที่หลุดไปแปลว่าคำตัดสินว่าแถวไหนผิดมาจาก client ไม่ใช่จากกฎของระบบ
 *
 * **บรรทัดก็ถูกประกอบใหม่ทีละคีย์เช่นกัน** ไม่ใช่ส่งต่อทั้ง object ที่ผู้เรียกถือ
 *
 * `source_file_hash` หายไปทั้งคีย์เมื่อไม่ได้ระบุ ไม่ใช่ส่ง `null` หรือสตริงว่าง —
 * `min_length=1` ฝั่งเซิร์ฟเวอร์จะตอบ `400` ให้กับสตริงว่าง ทั้งที่ผู้ใช้แค่ไม่ได้
 * กรอกช่องที่ไม่บังคับ
 */
export function serializeCreateForecastImportBody(
  body: CreateForecastImportBody,
): Record<string, unknown> {
  return {
    plant_code: body.plant_code,
    customer_code: body.customer_code,
    version_label: body.version_label,
    ...(body.source_file_hash === undefined
      ? {}
      : { source_file_hash: body.source_file_hash }),
    lines: body.lines.map((line) => ({
      line_no: line.line_no,
      product_code: line.product_code,
      forecast_date: line.forecast_date,
      quantity: line.quantity,
      uom_code: line.uom_code,
    })),
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | ForecastImportFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: ForecastImportRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** คำตอบของทั้งสามเส้นที่ contract สัญญาว่าคืน batch พร้อมบรรทัดครบ */
function toDetailResult(result: PayloadResult): ForecastImportDetailResult {
  if (result.status !== 'payload') {
    return result;
  }
  const batch = parseForecastImportBatch(result.payload);
  return batch === null ? { status: 'invalid-response' } : { status: 'ready', batch };
}

/**
 * `GET /v1/forecast-imports` — **กรองตามขอบเขตโรงงานของผู้เรียกเสมอ** และ
 * **ไม่มีบรรทัด**
 *
 * **ไม่เรียงลำดับซ้ำเอง** การเรียงซ้ำที่ frontend จะกลบกรณีที่เซิร์ฟเวอร์เรียงผิดจน
 * ไม่มีใครเห็น และจะทำให้ cursor ของหน้าถัดไปไม่ต่อกับสิ่งที่ผู้ใช้เห็นอยู่
 *
 * **ไม่กรอง `SUPERSEDED` ทิ้ง** — ใบที่ถูกแทนแล้วยังเป็นใบที่มีอยู่จริงและยังตอบ
 * คำถามว่าทำไมแผนของเดือนก่อนถึงเป็นอย่างนั้น (flow ข้อ 1.4) การซ่อนมันที่ client
 * คือการลบประวัติออกจากสายตาโดยที่ข้อมูลยังอยู่
 */
export async function fetchForecastImports(
  query: ForecastImportListQuery = {},
  options: ForecastImportRequestOptions = {},
): Promise<ForecastImportListResult> {
  const result = await requestPayload(
    `${FORECAST_IMPORTS_PATH}${buildForecastImportListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseForecastImportListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `GET /v1/forecast-imports/{batch_id}` — batch เดียว **พร้อมบรรทัดครบ**
 *
 * เส้นเดียวของกลุ่มนี้ที่ตอบคำถาม "แถวไหนในไฟล์ผิด และผิดเพราะอะไร" — รายการตัด
 * บรรทัดออกตาม contract ผู้เรียกจึงต้องมาที่นี่ **ทีละใบที่ผู้ใช้เปิดดูจริง** ไม่ใช่
 * วนเรียกให้ครบทุกแถวของรายการเพื่อประกอบตารางเดียว (N+1 คำขอที่ contract ตั้งใจ
 * หลีกเลี่ยงด้วยการตัดบรรทัดออกตั้งแต่แรก)
 *
 * ใบที่ไม่มีอยู่ หรืออยู่ในโรงงานนอกขอบเขตของผู้เรียก ตอบ `404` เหมือนกันทั้งคู่
 */
export async function fetchForecastImport(
  batchId: string,
  options: ForecastImportRequestOptions = {},
): Promise<ForecastImportDetailResult> {
  return toDetailResult(
    await requestPayload(
      forecastImportPath(batchId),
      { method: 'GET', headers: { Accept: 'application/json' } },
      options,
    ),
  );
}

/**
 * `POST /v1/forecast-imports` — นำไฟล์เข้าและ **ตรวจทั้งชุด** ตอบ `201`
 *
 * **นี่คือเฟสที่หนึ่ง และมันไม่สร้าง demand ใด ๆ เลย** (flow ข้อ 1.3) คำตอบคือ batch
 * ที่สถานะเป็น `VALIDATED` หรือ `INVALID` พร้อมทุกบรรทัด **รวมบรรทัดที่ผิด** — ต่าง
 * จากเส้น `POST` ของกลุ่มอื่นในระบบนี้ตรงที่ **`201` ไม่ได้แปลว่าทุกอย่างถูกต้อง**
 * มันแปลว่า "ไฟล์ถูกบันทึกไว้แล้ว และนี่คือผลการตรวจ" ผู้เรียกต้องอ่าน `status`
 * ต่อเสมอ ไม่ใช่ถือว่าเสร็จ
 *
 * **ไม่มี `Idempotency-Key`**: contract สงวน header นั้นไว้ให้ Scan, Goods Receipt,
 * Initial Import และ Offline Sync ที่กระทบ stock/บัญชี การนำ forecast เข้ามายังไม่
 * เคลื่อนสต๊อกหรือบัญชี และ `source_file_hash` **ไม่ใช่** idempotency key เช่นกัน —
 * flow ข้อ 1.4 คาดหวังให้ลูกค้ารายเดิมส่งไฟล์ที่แก้แล้วเข้ามาซ้ำได้เรื่อย ๆ
 */
export async function createForecastImport(
  body: CreateForecastImportBody,
  options: ForecastImportRequestOptions = {},
): Promise<ForecastImportDetailResult> {
  return toDetailResult(
    await requestPayload(
      FORECAST_IMPORTS_PATH,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(serializeCreateForecastImportBody(body)),
      },
      options,
    ),
  );
}

/**
 * `POST /v1/forecast-imports/{batch_id}/publish` — `VALIDATED → PUBLISHED` (APPROVE)
 *
 * **เฟสที่สอง และเป็นการกระทำที่แยกจากการสร้างโดยสิ้นเชิง** — flow ข้อ 1.4 ระบุว่า
 * "ผู้รับผิดชอบตรวจและ Publish" สิทธิ์จึงเป็น APPROVE ไม่ใช่ CREATE และหน้าจอที่
 * สร้างแล้ว publish ให้ในคลิกเดียวคือหน้าจอที่ลบขั้นตอนการตรวจนั้นทิ้ง
 *
 * ผลข้างเคียงที่ contract สัญญาไว้และเกิดในธุรกรรมเดียวกัน: batch ที่ `PUBLISHED`
 * อยู่ก่อนของคู่ (โรงงาน, ลูกค้า) เดียวกันกลายเป็น `SUPERSEDED` และชี้
 * `superseded_by_batch_id` มาที่ใบใหม่ — **ไม่เขียนทับ ไม่ลบ**
 *
 * `INVALID` ตอบ `422 FORECAST_BATCH_IS_INVALID` ส่วนสถานะอื่นตอบ `409` พร้อม
 * `details.current_status`
 *
 * **ไม่ส่ง body ใด ๆ ไปเลย** — contract ไม่ได้ประกาศฟิลด์ใดในคำขอของเส้นนี้ และคีย์
 * ที่ contract ไม่ได้ประกาศ คือคีย์ที่ `extra = "forbid"` จะตอบ `400` ให้ ปุ่มที่ใช้
 * งานไม่ได้เลยแม้แต่ครั้งเดียวแย่กว่าการไม่มีด่าน staleness ฝั่ง client
 */
export async function publishForecastImport(
  batchId: string,
  options: ForecastImportRequestOptions = {},
): Promise<ForecastImportDetailResult> {
  return toDetailResult(
    await requestPayload(
      forecastImportPublishPath(batchId),
      { method: 'POST', headers: { Accept: 'application/json' } },
      options,
    ),
  );
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย
 */
export function ruleFromFailure(failure: ForecastImportFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}

/**
 * อ่าน `details.current_status` จากความล้มเหลว — สถานะที่ batch นั้น **ถืออยู่จริง**
 *
 * contract สัญญาว่า `409` ของ publish พามันมาด้วยเสมอ และมันสำคัญกว่ารหัสกฎในกรณี
 * นี้: `SUPERSEDED` แปลว่ามีคนนำ revision ใหม่เข้ามาแล้ว ส่วน `PUBLISHED` แปลว่ามี
 * คนกดปุ่มเดียวกันนี้ไปก่อนแล้ว — สองเรื่องที่ผู้ใช้ทำต่อคนละอย่าง
 */
export function currentStatusFromFailure(
  failure: ForecastImportFailure,
): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}
